import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CollectionsIcon from '@mui/icons-material/Collections';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from './UserContext';
import api from './api';

const Dashboard = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { clubShortName, contestShortName, name, isGallery }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const response = await api.get('/clubs', {
          headers: {
            'Authentication': user.authentication
          }
        });
        setClubs(response.data.result);
      } catch (err) {
        setError('Failed to load clubs');
        console.error('Error fetching clubs:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.authentication) {
      fetchClubs();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleCreateClub = () => {
    navigate('/createclub');
  };

  const getContestStatus = (contest) => {
    const now = new Date();
    const uploadsStart = new Date(contest.contestUploadsStart * 1000);
    const uploadsEnd = new Date(contest.contestUploadsEnd * 1000);
    const votesStart = new Date(contest.contestVotesStart * 1000);
    const votesEnd = new Date(contest.contestVotesEnd * 1000);

    if (now < uploadsStart) {
      return { status: 'Upcoming', color: 'default' };
    } else if (now >= uploadsStart && now <= uploadsEnd) {
      return { status: 'Uploads Open', color: 'primary' };
    } else if (now > uploadsEnd && now < votesStart) {
      return { status: 'Awaiting Voting', color: 'warning' };
    } else if (now >= votesStart && now <= votesEnd) {
      return { status: 'Voting Open', color: 'success' };
    } else if (contest.contestShowResults) {
      return { status: 'Contest Results', color: 'secondary' };
    } else {
      return { status: 'Awaiting Results', color: 'warning' };
    }
  };

  const getGalleryStatus = (gallery) => {
    if (!gallery.contestGalleryEndDate) return { status: 'Active', color: 'success' };
    const now = Math.floor(Date.now() / 1000);
    if (now >= gallery.contestGalleryEndDate) return { status: 'Closed', color: 'default' };
    return { status: 'Active', color: 'success' };
  };

  const canCreateContest = (club) => {
    return club.userRole === 'owner' && club.clubStripeSubscriptionStatus !== 'canceled';
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/club/${deleteTarget.clubShortName}/contest/${deleteTarget.contestShortName}`, {
        headers: { 'Authentication': user.authentication }
      });
      setClubs(prev => prev.map(club => {
        if (club.clubShortName !== deleteTarget.clubShortName) return club;
        return { ...club, contests: (club.contests || []).filter(ct => ct.contestShortName !== deleteTarget.contestShortName) };
      }));
    } catch (err) {
      console.error('Delete failed', err);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Box sx={{
      padding: 3,
      position: 'relative',
      minHeight: '100vh'
    }}>
      {/* Background mascot */}
      <Box sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        opacity: 0.1,
        pointerEvents: 'none',
        zIndex: 0
      }}>
        <img src='/mascot.png' alt='' style={{ width: '100%', height: 'auto' }} />
      </Box>

      {/* Main content */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
          Dashboard
        </Typography>

        <Grid container spacing={3} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Create Club Card */}
          {!loading && clubs.length === 0 && (
            <Grid item xs={12} sm={6} md={4}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  }
                }}
                onClick={handleCreateClub}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 4 }}>
                  <AddIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Add a Club
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add your Club so that you can organize photography contests for your members.
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                  <Button variant="contained" size="large" startIcon={<AddIcon />}>
                    Get Started
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          )}

          {/* My Clubs Card - Only show if there are clubs */}
          {!loading && clubs.length > 0 && (
            <Grid item xs={12} sm={6} md={4}>
              <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h5">
                      My Clubs
                    </Typography>
                  </Box>
                  <List dense>
                    {clubs.map((club) => (
                      <ListItem
                        key={club.clubGuid}
                        component={Link}
                        to={`/club/${club.clubShortName}`}
                        sx={{
                          textDecoration: 'none',
                          color: 'inherit',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <ListItemText
                          primary={`${club.clubName} (${club.userRole})`}
                          secondary={
                            <>
                              @{club.clubShortName}
                              {club.clubStripeCancelsAtPeriodEnd ? (
                                <>
                                  <br />
                                  <span style={{fontStyle: 'italic'}}>
                                    {`Subscription ends ${new Date(club.clubStripeCurrentPeriodEnds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                                  </span>
                                </>
                              ) : club.clubStripeSubscriptionStatus === 'trialing' ? (
                                <>
                                  <br />
                                  <span style={{fontStyle: 'italic'}}>
                                    {`Trial ends ${new Date(club.clubStripeCurrentPeriodEnds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                                  </span>
                                </>
                              ) : club.clubStripeSubscriptionStatus === 'canceled' ? (
                                <>
                                  <br />
                                  <span style={{fontStyle: 'italic', fontSize: '0.8em', position: 'relative', top: '-5px', color: 'red'}}>
                                    Subscription cancelled
                                  </span>
                                </>
                              ) : null}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                  <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={handleCreateClub}>
                      Add a Club
                    </Button>
                  </CardActions>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* My Contests Card - contests only (no galleries) */}
          {!loading && clubs.length > 0 && (
            <Grid item xs={12} sm={6} md={4}>
              <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CameraAltIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h5">
                      My Contests
                    </Typography>
                  </Box>

                  {clubs.every(club => !(club.contests || []).some(ct => !ct.contestIsGallery)) ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No contests available
                    </Typography>
                  ) : (
                    <List dense>
                      {clubs.map(club =>
                        (club.contests || []).filter(ct => !ct.contestIsGallery).map(contest => {
                          const { status, color } = getContestStatus(contest);
                          return (
                            <ListItem
                              key={contest.contestGuid}
                              component={Link}
                              to={`/club/${club.clubShortName}/contest/${contest.contestShortName}`}
                              sx={{
                                textDecoration: 'none',
                                color: 'inherit',
                                borderRadius: 1,
                                '&:hover': { backgroundColor: 'action.hover' },
                                mb: 1
                              }}
                            >
                              <ListItemText
                                primary={contest.contestName}
                                secondary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                    <Chip label={status} size="small" color={color} sx={{ mr: 1 }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {club.clubName} • @{contest.contestShortName}
                                    </Typography>
                                  </Box>
                                }
                                slotProps={{ secondary: { component: 'div' } }}
                              />
                            </ListItem>
                          );
                        })
                      )}
                    </List>
                  )}

                  {clubs.filter(club => canCreateContest(club)).map(club => (
                    <Box key={`create-${club.clubGuid}`} sx={{ mt: 2 }}>
                      <Button
                        component={Link}
                        to={`/club/${club.clubShortName}/createcontest`}
                        variant="outlined"
                        startIcon={<AddIcon />}
                        fullWidth
                        size="small"
                      >
                        Create Contest for {club.clubName}
                      </Button>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* My Galleries Card */}
          {!loading && clubs.length > 0 && (
            <Grid item xs={12} sm={6} md={4}>
              <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CollectionsIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h5">
                      My Galleries
                    </Typography>
                  </Box>

                  {clubs.every(club => !(club.contests || []).some(ct => ct.contestIsGallery)) ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No galleries available
                    </Typography>
                  ) : (
                    <List dense>
                      {clubs.map(club =>
                        (club.contests || []).filter(ct => ct.contestIsGallery).map(gallery => {
                          const { status, color } = getGalleryStatus(gallery);
                          return (
                            <ListItem
                              key={gallery.contestGuid}
                              component={Link}
                              to={`/club/${club.clubShortName}/contest/${gallery.contestShortName}`}
                              sx={{
                                textDecoration: 'none',
                                color: 'inherit',
                                borderRadius: 1,
                                '&:hover': { backgroundColor: 'action.hover' },
                                mb: 1
                              }}
                            >
                              <ListItemText
                                primary={gallery.contestName}
                                secondary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                    <Chip label={status} size="small" color={color} sx={{ mr: 1 }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {club.clubName} • @{gallery.contestShortName}
                                    </Typography>
                                  </Box>
                                }
                                slotProps={{ secondary: { component: 'div' } }}
                              />
                            </ListItem>
                          );
                        })
                      )}
                    </List>
                  )}

                  {clubs.filter(club => canCreateContest(club)).map(club => (
                    <Box key={`creategallery-${club.clubGuid}`} sx={{ mt: 2 }}>
                      <Button
                        component={Link}
                        to={`/club/${club.clubShortName}/creategallery`}
                        variant="outlined"
                        startIcon={<AddIcon />}
                        fullWidth
                        size="small"
                      >
                        Create Gallery for {club.clubName}
                      </Button>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Contest & Gallery Management Card */}
          {!loading && clubs.length > 0 && (
            <Grid item xs={12} sm={6} md={4}>
              <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5">
                      Management
                    </Typography>
                  </Box>

                  {(() => {
                    const isEditable = (club, item) =>
                      club.clubStripeSubscriptionStatus !== 'canceled' &&
                      (club.userRole === 'owner' ||
                       (item.contestCommittee && item.contestCommittee.includes(user.userEmail)));

                    const editableContests = clubs.flatMap(club =>
                      (club.contests || [])
                        .filter(ct => !ct.contestIsGallery && isEditable(club, ct))
                        .map(ct => ({ ...ct, clubName: club.clubName, clubShortName: club.clubShortName, isOwner: club.userRole === 'owner' }))
                    );

                    const editableGalleries = clubs.flatMap(club =>
                      (club.contests || [])
                        .filter(ct => ct.contestIsGallery && isEditable(club, ct))
                        .map(ct => ({ ...ct, clubName: club.clubName, clubShortName: club.clubShortName, isOwner: club.userRole === 'owner' }))
                    );

                    const nothingEditable = editableContests.length === 0 && editableGalleries.length === 0;

                    return nothingEditable ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No contests or galleries to manage
                      </Typography>
                    ) : (
                      <>
                        {editableContests.length > 0 && (
                          <>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                              Contests
                            </Typography>
                            <List dense>
                              {editableContests.map(contest => (
                                <ListItem
                                  key={contest.contestGuid}
                                  component={Link}
                                  to={`/club/${contest.clubShortName}/contest/${contest.contestShortName}/edit`}
                                  sx={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    borderRadius: 1,
                                    '&:hover': { backgroundColor: 'action.hover' },
                                    mb: 1,
                                    pr: contest.isOwner ? 6 : 2,
                                  }}
                                >
                                  <ListItemText
                                    primary={contest.contestName}
                                    secondary={
                                      <Typography variant="caption" color="text.secondary">
                                        {contest.clubName} • Edit Contest
                                      </Typography>
                                    }
                                  />
                                  {contest.isOwner && (
                                    <ListItemSecondaryAction>
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        color="error"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setDeleteTarget({ clubShortName: contest.clubShortName, contestShortName: contest.contestShortName, name: contest.contestName, isGallery: false });
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </ListItemSecondaryAction>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}
                        {editableGalleries.length > 0 && (
                          <>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                              Galleries
                            </Typography>
                            <List dense>
                              {editableGalleries.map(gallery => (
                                <ListItem
                                  key={gallery.contestGuid}
                                  component={Link}
                                  to={`/club/${gallery.clubShortName}/contest/${gallery.contestShortName}/edit`}
                                  sx={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    borderRadius: 1,
                                    '&:hover': { backgroundColor: 'action.hover' },
                                    mb: 1,
                                    pr: gallery.isOwner ? 6 : 2,
                                  }}
                                >
                                  <ListItemText
                                    primary={gallery.contestName}
                                    secondary={
                                      <Typography variant="caption" color="text.secondary">
                                        {gallery.clubName} • Edit Gallery
                                      </Typography>
                                    }
                                  />
                                  {gallery.isOwner && (
                                    <ListItemSecondaryAction>
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        color="error"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setDeleteTarget({ clubShortName: gallery.clubShortName, contestShortName: gallery.contestShortName, name: gallery.contestName, isGallery: true });
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </ListItemSecondaryAction>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Loading and Error States */}
        {loading && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body1">Loading clubs...</Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body1" color="error">
              {error}
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete {deleteTarget?.isGallery ? 'Gallery' : 'Contest'}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{deleteTarget?.name}</strong>? All photos and votes will be deleted and cannot be recovered.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;